import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  Check,
  Globe,
  Loader2,
  Sparkles,
  Edit3,
  Rocket,
  ChevronLeft,
} from "lucide-react";
import { NadiaAvatar } from "@/components/nadia/NadiaAvatar";
import { useCreditGate } from "@/components/nadia/CreditGate";
import { InstagramPreview, FacebookPreview } from "@/components/nadia/AdPreview";
import { CreativeVariants } from "@/components/nadia/CreativeVariants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  analyzeUrl,
  createGoal,
  getPlan,
  patchPlan,
  generateCreatives,
  patchCreatives,
  publishCreatives,
} from "@/lib/api";

const STEPS = [
  "Attività individuata",
  "Prodotti e servizi",
  "Offerte",
  "Pubblico potenziale",
  "Tono del brand",
  "Immagini disponibili",
  "Contatti",
  "Località",
];

const PHASES = [
  { id: "url", label: "Sito" },
  { id: "analysis", label: "Analisi" },
  { id: "goal", label: "Obiettivo" },
  { id: "plan", label: "Piano" },
  { id: "creative", label: "Pubblicità" },
];

const GOAL_SUGGESTIONS = [
  "30 nuove prenotazioni",
  "50 registrazioni",
  "20 nuovi clienti",
  "€5.000 di vendite",
];

export default function Onboarding() {
  const nav = useNavigate();
  const [phase, setPhase] = useState("url");
  const [url, setUrl] = useState("");
  const [progress, setProgress] = useState(0);
  const [profile, setProfile] = useState(null);
  const [goalText, setGoalText] = useState("");
  const [plan, setPlan] = useState(null);
  const [creative, setCreative] = useState(null);
  const [busy, setBusy] = useState(false);

  const phaseIdx = PHASES.findIndex((p) => p.id === phase);

  // ---- Phase 1: URL analysis ----
  const { gate, dialog: creditDialog } = useCreditGate();
  const runAnalysis = () => {
    if (!url.trim()) return toast.error("Inserisci l'URL del tuo sito");
    return gate("site_analysis", (op_id) => doAnalysis(op_id));
  };
  const doAnalysis = async (op_id) => {
    setPhase("analysis");
    setProgress(0);
    const backendCall = analyzeUrl(url, op_id).catch((e) => { if (e?.response?.status === 402) { toast.error(e.response.data?.detail); setPhase("url"); } return null; });
    for (let i = 0; i < STEPS.length; i++) {
      await new Promise((r) => setTimeout(r, 320));
      setProgress(i + 1);
    }
    const res = await backendCall;
    setProfile(res?.profile || null);
  };

  // ---- Phase 2: Goal ----
  const goToGoal = () => setPhase("goal");

  const submitGoal = async () => {
    if (!goalText.trim()) return toast.error("Descrivi il tuo obiettivo");
    setBusy(true);
    try {
      await createGoal({ natural_language: goalText });
      const p = await getPlan();
      setPlan(p);
      setPhase("plan");
    } catch {
      toast.error("Errore, riprova");
    } finally {
      setBusy(false);
    }
  };

  // ---- Phase 3: Plan ----
  const savePlan = async (patch) => {
    setPlan({ ...plan, ...patch });
    patchPlan(patch).catch(() => {});
  };

  const goToCreative = async () => {
    setBusy(true);
    setPhase("creative");
    try {
      const c = await generateCreatives();
      setCreative(c);
    } catch {
      toast.error("Nadia non riesce a preparare la pubblicità");
    } finally {
      setBusy(false);
    }
  };

  // ---- Phase 4: Creative + Publish ----
  const saveCreative = async (patch) => {
    setCreative({ ...creative, ...patch });
    patchCreatives(patch).catch(() => {});
  };

  const publish = async () => {
    setBusy(true);
    try {
      const r = await publishCreatives();
      const approvedLocally = r?.publication_state === "approvato"
        || (r?.published === false && r?.approved === true);
      toast.success(approvedLocally
        ? "Annuncio approvato localmente. Pubblicazione non confermata."
        : "Richiesta elaborata. Stato operativo non confermato.");
      nav("/app");
    } catch {
      toast.error("Non riesco a confermare l'esito della richiesta di approvazione.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-8 lg:py-12">
      {creditDialog}
      <div className="max-w-5xl mx-auto">
        <Stepper phaseIdx={phaseIdx} />

        {/* Hero header */}
        <div className="flex items-start gap-4 mt-8 mb-8">
          <NadiaAvatar size="lg" thinking={phase === "analysis" || busy} />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300/80">
              Nadia Cloud · Direttore Marketing AI
            </div>
            <h1 className="mt-2 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]">
              {phase === "url" && <>Un unico URL, <span className="text-ai-gradient">tutto il resto lo faccio io</span>.</>}
              {phase === "analysis" && <>Sto studiando <span className="text-ai-gradient">la tua attività</span>.</>}
              {phase === "goal" && <>Cosa vuoi ottenere <span className="text-ai-gradient">questo mese?</span></>}
              {phase === "plan" && <>Il piano che ho <span className="text-ai-gradient">preparato per te</span>.</>}
              {phase === "creative" && <>Ecco come apparirà <span className="text-ai-gradient">l'annuncio</span>.</>}
            </h1>
            <p className="mt-2 text-slate-400 text-sm sm:text-base max-w-2xl">
              {phase === "url" && "Incolla il sito della tua azienda. Da lì costruisco Business Brain, piano marketing e creatività pubblicitaria — tutto collegato."}
              {phase === "analysis" && "Estraggo attività, prodotti, offerte, pubblico, tono, immagini, CTA e prenotazioni."}
              {phase === "goal" && "In linguaggio naturale. Trasformo la tua richiesta in un piano concreto."}
              {phase === "plan" && "Tutto è modificabile prima di andare avanti. Se qualcosa non ti convince, cambialo qui."}
              {phase === "creative" && "Modifica headline, testo e call to action. L'anteprima si aggiorna in tempo reale."}
            </p>
          </div>
        </div>

        {/* Phase content */}
        {phase === "url" && (
          <PhaseCard>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Sito della tua attività</label>
            <div className="mt-3 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.esempio.com"
                  className="pl-10 h-12 bg-[#0B1120] border-white/10 focus-visible:ring-cyan-400 focus-visible:ring-offset-0 text-slate-100"
                  data-testid="url-analysis-input"
                />
              </div>
              <Button
                onClick={runAnalysis}
                className="h-12 px-6 bg-gradient-to-r from-cyan-400 to-violet-500 hover:from-cyan-300 hover:to-violet-400 text-slate-950 font-semibold rounded-xl"
                data-testid="analyze-url-button"
              >
                <Sparkles size={16} className="mr-2" /> Analizza attività
              </Button>
            </div>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-slate-500">
              {["Business Brain", "Piano Marketing", "Creatività", "Anteprima FB/IG"].map((t) => (
                <div key={t} className="rounded-lg border border-white/5 bg-white/[0.02] py-2 px-3">
                  <span className="text-cyan-300">◆</span> {t}
                </div>
              ))}
            </div>
          </PhaseCard>
        )}

        {phase === "analysis" && (
          <PhaseCard>
            <StepsList progress={progress} />
            {profile && progress === STEPS.length && (
              <div className="mt-6 pt-6 border-t border-white/5">
                <div className="text-xs text-cyan-300 uppercase tracking-wider font-semibold mb-3">
                  Business Profile
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                  <Field label="Nome" value={profile.business} />
                  <Field label="Settore" value={profile.industry} />
                  <Field label="Località" value={profile.locations?.join(", ")} />
                  <Field label="Tono" value={profile.brandTone} />
                  <Field label="Target" value={profile.targetAudience?.[0]} />
                  <Field label="CTA" value={profile.cta_primary} />
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={goToGoal}
                    className="h-11 px-5 bg-gradient-to-r from-cyan-400 to-violet-500 hover:from-cyan-300 hover:to-violet-400 text-slate-950 font-semibold rounded-xl"
                    data-testid="continue-to-goal-button"
                  >
                    Definisci l'obiettivo <ArrowRight size={15} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}
          </PhaseCard>
        )}

        {phase === "goal" && (
          <PhaseCard>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Descrivi il tuo obiettivo</label>
            <textarea
              value={goalText}
              onChange={(e) => setGoalText(e.target.value)}
              rows={3}
              placeholder="Voglio 30 nuove registrazioni questo mese con massimo €300."
              className="mt-3 w-full bg-[#0B1120] border border-white/10 rounded-xl px-4 py-3 text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/60 resize-none"
              data-testid="goal-input"
            />
            <div className="mt-4 flex flex-wrap gap-2">
              {GOAL_SUGGESTIONS.map((g) => (
                <button
                  key={g}
                  onClick={() => setGoalText(g)}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-200 transition-colors"
                >
                  {g}
                </button>
              ))}
            </div>
            <div className="mt-6 flex justify-between">
              <Button
                variant="ghost"
                onClick={() => setPhase("analysis")}
                className="text-slate-400 hover:text-slate-200"
              >
                <ChevronLeft size={15} className="mr-1" /> Indietro
              </Button>
              <Button
                onClick={submitGoal}
                disabled={busy}
                className="h-11 px-5 bg-gradient-to-r from-cyan-400 to-violet-500 hover:from-cyan-300 hover:to-violet-400 text-slate-950 font-semibold rounded-xl"
                data-testid="submit-goal-button"
              >
                {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Sparkles size={15} className="mr-2" />}
                Prepara il piano
              </Button>
            </div>
          </PhaseCard>
        )}

        {phase === "plan" && plan && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <EditableRow testId="plan-goal" label="Obiettivo commerciale" value={plan.commercial_goal} onChange={(v) => savePlan({ commercial_goal: v })} />
            <EditableRow testId="plan-offer" label="Offerta da promuovere" value={plan.offer} onChange={(v) => savePlan({ offer: v })} />
            <EditableRow testId="plan-audience" label="Pubblico" value={plan.audience} onChange={(v) => savePlan({ audience: v })} />
            <EditableRow testId="plan-geo" label="Area geografica" value={plan.geographic_area} onChange={(v) => savePlan({ geographic_area: v })} />
            <EditableRow testId="plan-channel" label="Canale" value={(plan.channels || []).join(", ")} onChange={(v) => savePlan({ channels: v.split(",").map((x) => x.trim()) })} />
            <EditableRow testId="plan-budget" label="Budget mensile (€)" value={String(plan.budget_monthly ?? "")} onChange={(v) => savePlan({ budget_monthly: Number(v) || 0 })} />
            <EditableRow testId="plan-duration" label="Durata (giorni)" value={String(plan.duration_days ?? "")} onChange={(v) => savePlan({ duration_days: Number(v) || 30 })} />
            <EditableRow testId="plan-landing" label="Landing page" value={plan.landing_page} onChange={(v) => savePlan({ landing_page: v })} />

            <div className="lg:col-span-2 flex justify-between mt-2">
              <Button variant="ghost" onClick={() => setPhase("goal")} className="text-slate-400 hover:text-slate-200">
                <ChevronLeft size={15} className="mr-1" /> Indietro
              </Button>
              <Button
                onClick={goToCreative}
                className="h-11 px-5 bg-gradient-to-r from-cyan-400 to-violet-500 hover:from-cyan-300 hover:to-violet-400 text-slate-950 font-semibold rounded-xl"
                data-testid="continue-to-creative-button"
              >
                <Sparkles size={15} className="mr-2" /> Prepara la pubblicità
              </Button>
            </div>
          </div>
        )}

        {phase === "creative" && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
            {/* Left: editable creative fields */}
            <div className="space-y-3">
              {!creative ? (
                <div className="nadia-card p-8 flex items-center gap-3 text-slate-400">
                  <Loader2 size={16} className="animate-spin" /> Nadia sta scrivendo l'annuncio…
                </div>
              ) : (
                <>
                  <CreativeVariants creative={creative} onPick={saveCreative} />
                  <EditableRow testId="c-headline" label="Headline" value={creative.headline} onChange={(v) => saveCreative({ headline: v })} />
                  <EditableRow testId="c-primary" label="Testo principale" value={creative.primary_text} onChange={(v) => saveCreative({ primary_text: v })} multiline />
                  <EditableRow testId="c-cta" label="Call to action" value={creative.cta} onChange={(v) => saveCreative({ cta: v.toUpperCase() })} />
                  <EditableRow testId="c-target" label="Target" value={creative.target_summary} onChange={(v) => saveCreative({ target_summary: v })} />
                  <EditableRow testId="c-geo" label="Zona" value={creative.geographic_area} onChange={(v) => saveCreative({ geographic_area: v })} />
                  <div className="grid grid-cols-2 gap-3">
                    <EditableRow testId="c-budget" label="Budget/mese (€)" value={String(creative.budget_monthly ?? "")} onChange={(v) => saveCreative({ budget_monthly: Number(v) || 0 })} />
                    <EditableRow testId="c-dur" label="Durata (giorni)" value={String(creative.duration_days ?? "")} onChange={(v) => saveCreative({ duration_days: Number(v) || 30 })} />
                  </div>
                  <EditableRow testId="c-dest" label="URL destinazione" value={creative.destination_url} onChange={(v) => saveCreative({ destination_url: v })} />
                  <EditableRow testId="c-video" label="Idea video / reel" value={creative.video_prompt} onChange={(v) => saveCreative({ video_prompt: v })} multiline />
                  <a
                    href="/app/video"
                    className="nadia-card p-4 flex items-center gap-3 border-violet-500/25 hover:border-violet-400/50 transition-colors"
                    data-testid="open-video-studio"
                  >
                    <span className="w-9 h-9 rounded-lg bg-violet-500/10 text-violet-300 flex items-center justify-center shrink-0"><Sparkles size={16} /></span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-100">Vuoi anche un Reel o un video pubblicitario?</span>
                      <span className="block text-xs text-slate-400">Nadia lo prepara nel Video / Reel Studio con i dati che ha già raccolto dal tuo sito.</span>
                    </span>
                  </a>

                  <div className="flex justify-between pt-3">
                    <Button variant="ghost" onClick={() => setPhase("plan")} className="text-slate-400 hover:text-slate-200">
                      <ChevronLeft size={15} className="mr-1" /> Piano
                    </Button>
                    <Button
                      onClick={publish}
                      disabled={busy}
                      className="h-11 px-5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-xl"
                      data-testid="publish-button"
                    >
                      {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Rocket size={15} className="mr-2" />}
                      Approva e pubblica
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Right: FB + IG live preview */}
            <div className="space-y-4 lg:sticky lg:top-6">
              <div className="text-[11px] uppercase tracking-wider text-cyan-300 font-semibold">Anteprima in tempo reale</div>
              {creative && (
                <>
                  <InstagramPreview creative={creative} />
                  <FacebookPreview creative={creative} />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stepper({ phaseIdx }) {
  return (
    <div className="flex items-center gap-2 sm:gap-3 flex-wrap" data-testid="onboarding-stepper">
      {PHASES.map((p, i) => {
        const done = i < phaseIdx;
        const active = i === phaseIdx;
        return (
          <div key={p.id} className="flex items-center gap-2 sm:gap-3">
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${
                active
                  ? "bg-cyan-500/10 text-cyan-200 border-cyan-500/30"
                  : done
                  ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/25"
                  : "bg-white/[0.02] text-slate-500 border-white/10"
              }`}
            >
              <span className="inline-flex w-4 h-4 items-center justify-center rounded-full text-[9px]">
                {done ? <Check size={10} strokeWidth={3} /> : i + 1}
              </span>
              {p.label}
            </div>
            {i < PHASES.length - 1 && <div className="w-4 sm:w-8 h-px bg-white/10" />}
          </div>
        );
      })}
    </div>
  );
}

function PhaseCard({ children }) {
  return <div className="nadia-card p-5 sm:p-7">{children}</div>;
}

function StepsList({ progress }) {
  return (
    <div className="space-y-2">
      {STEPS.map((s, i) => {
        const done = i < progress;
        const active = i === progress;
        return (
          <div
            key={s}
            className={`flex items-center gap-3 py-1.5 ${
              done ? "text-slate-100 step-enter" : "text-slate-500"
            }`}
          >
            <span
              className={`inline-flex items-center justify-center w-5 h-5 rounded-full border ${
                done
                  ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-400"
                  : active
                  ? "border-cyan-400/50 text-cyan-400"
                  : "border-white/10 text-slate-600"
              }`}
            >
              {done ? <Check size={12} strokeWidth={3} /> : active ? <Loader2 size={11} className="animate-spin" /> : null}
            </span>
            <span className="text-sm">{s}</span>
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-200 font-medium">{value || "—"}</div>
    </div>
  );
}

function EditableRow({ label, value, onChange, multiline, testId }) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="nadia-card p-4 group" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
        <button
          onClick={() => setEditing((e) => !e)}
          className="text-slate-500 hover:text-cyan-300 transition-colors opacity-0 group-hover:opacity-100"
        >
          <Edit3 size={13} />
        </button>
      </div>
      {editing ? (
        multiline ? (
          <Textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onBlur={() => setEditing(false)}
            autoFocus
            rows={3}
            className="bg-[#0B1120] border-white/10 text-slate-100"
          />
        ) : (
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onBlur={() => setEditing(false)}
            autoFocus
            className="bg-[#0B1120] border-white/10 text-slate-100 h-10"
          />
        )
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="w-full text-left text-sm text-slate-100 min-h-[24px] hover:text-cyan-200 transition-colors"
        >
          {value || <span className="italic text-slate-500">tocca per aggiungere</span>}
        </button>
      )}
    </div>
  );
}
